import { useCallback, useEffect, useRef } from 'react';

import { REQUIRED_PHONE_VERIFICATION } from '@/consts';
import { hasViewerSession } from '@/components/OpenViewer/helpers';
import {
    SharedFile, FilesAccessStore, PermissionsState, SharedFilesStore,
} from '../../../../../../../stores';
import { OpenOptions } from '../../../../../../../config/openOptions';
import { SimpleCallback } from '../../../../../../../types/types';
import { SelectedFileViewState } from './interfaces';
import { ERRORS_FOR_NEW_ATTEMPTS, getSelectedFileViewState } from './helpers';

const hasSuccessfulViewerResult = (
    viewerState: SelectedFileViewState['viewerState'],
): boolean => hasViewerSession(viewerState?.result);

const viewerStateNeedsFetch = (
    viewerState: SelectedFileViewState['viewerState'],
    isFolder: boolean,
): boolean => {
    if (isFolder) return false;
    if (!viewerState) return true;
    if (ERRORS_FOR_NEW_ATTEMPTS.includes(viewerState.errorType)) return true;
    return false;
};

export const useSignatureSaving = (filesAccessStore: FilesAccessStore): SimpleCallback => {
    const hasChangesRef = useRef<boolean>(false);

    const onSaveSignature = useCallback(() => {
        hasChangesRef.current = true;
    }, []);

    useEffect(() => {
        const fileId = filesAccessStore.selectedAccessFileId;
        return (): void => {
            if (hasChangesRef.current) {
                filesAccessStore.deleteAccessItem(fileId);
            }
        };
    }, []);

    return onSaveSignature;
};

interface UseOpenFileArgs {
    filesAccessStore: FilesAccessStore;
    sharedFilesStore: SharedFilesStore;
    selectedFilePermissions: PermissionsState;
    selectedFile: SharedFile;
}

export const useViewerState = ({
    filesAccessStore,
    sharedFilesStore,
    selectedFilePermissions,
    selectedFile,
}: UseOpenFileArgs): SelectedFileViewState => {
    const fetchedForFileRef = useRef<string | null>(null);
    const {
        selectedItemState,
        selectedAccessFileId,
    } = filesAccessStore;

    const {
        contentType,
        selectedFilePermissionsSummary,
        viewerState,
        errorType,
    } = getSelectedFileViewState(selectedFilePermissions, selectedFile, selectedItemState);

    useEffect(() => {
        if (selectedFilePermissionsSummary !== 'permissionsAdded') {
            return;
        }
        const alreadyHasResult = hasSuccessfulViewerResult(viewerState);
        if (alreadyHasResult) {
            fetchedForFileRef.current = selectedAccessFileId;
        }
        const needsFetch = viewerStateNeedsFetch(viewerState, !!selectedFile?.is_folder);
        const shouldOpen = !(selectedFile?.is_folder)
            && selectedFilePermissions?.summary === 'permissionsAdded'
            && !alreadyHasResult
            && needsFetch;
        // While the file is still being secured (408 -> 'fileBeingPrepared') the
        // "Still securing file" screen owns retries; refreshing permissions here is
        // pointless and its store write used to re-trigger LinkView's MFA probe,
        // causing an infinite greeting <-> securing-screen remount loop.
        const isBeingPrepared = viewerState?.errorType === 'fileBeingPrepared';
        if (!alreadyHasResult && !isBeingPrepared) {
            sharedFilesStore.tryRefreshPermissions(selectedAccessFileId);
        }
        if (shouldOpen) {
            filesAccessStore.tryFetchAccessLink({
                fileId: selectedAccessFileId,
                accessType: OpenOptions.viewer,
                alwaysCheckMFA: !!sharedFilesStore.allFilesPoliciesAggregate?.[REQUIRED_PHONE_VERIFICATION],
            });
        }
    }, [selectedFilePermissionsSummary, selectedAccessFileId, viewerState]);

    useEffect(() => {
        if (selectedAccessFileId !== fetchedForFileRef.current) {
            fetchedForFileRef.current = null;
        }
    }, [selectedAccessFileId]);

    return {
        contentType,
        errorType,
        viewerState,
    };
};
